/**
 * Central TON / contract configuration, sourced from Vite env vars.
 * See `.env.example` for the full list. Nothing here is secret — the API key
 * is optional and only raises rate limits for public read endpoints.
 */

const env: Partial<ImportMetaEnv> = import.meta.env ?? {};

export const TON_NETWORK: 'mainnet' | 'testnet' =
  env.VITE_TON_NETWORK === 'mainnet' ? 'mainnet' : 'testnet';

export const NATIVE_COIN_SYMBOL = 'GRAM';

export const CONTRACT_ADDRESS: string = env.VITE_CONTRACT_ADDRESS ?? '';

export const TON_API_ENDPOINT: string =
  env.VITE_TON_API_ENDPOINT ??
  (TON_NETWORK === 'mainnet'
    ? 'https://toncenter.com/api/v2/jsonRPC'
    : 'https://testnet.toncenter.com/api/v2/jsonRPC');

export const TON_API_KEY: string = env.VITE_TON_API_KEY ?? '';

/**
 * Optional backend proxy base URL (e.g. http://localhost:8787). When set, the
 * app reads the global state and per-wallet data through the caching proxy
 * (constant RPC cost regardless of audience) instead of hitting toncenter
 * directly from every browser. Leave empty to read straight from RPC.
 */
export const API_BASE: string = (env.VITE_API_BASE ?? '').replace(/\/$/, '');

export const USE_PROXY = API_BASE.length > 0;

export const MANIFEST_URL: string =
  env.VITE_MANIFEST_URL ??
  `${typeof window === 'undefined' ? 'http://localhost' : window.location.origin}/tonconnect-manifest.json`;

export const TICKET_PRICE_TON: number = Number(
  env.VITE_TICKET_PRICE_TON ?? '1',
);

export const IS_CONTRACT_CONFIGURED = CONTRACT_ADDRESS.length > 0;

/**
 * Pyramida Mentor AI-assistant bot — where users go to learn how to grow their
 * referral network. Configurable so the handle can change without a code edit.
 */
export const TEAM_BOT_URL: string =
  env.VITE_TEAM_BOT_URL?.replace(/\/+$/, '') || 'https://t.me/prmd_team_bot';

/**
 * Human support chat bot — users DM questions in any language, the owner
 * answers from the admin with AI translation both ways.
 */
export const SUPPORT_BOT_URL: string =
  env.VITE_SUPPORT_BOT_URL?.replace(/\/+$/, '') || 'https://t.me/prmd_support_bot';

/**
 * On-chain swap configuration.
 *
 * `SWAP_PRMD_MASTER` is the PRMD jetton master on mainnet — the token users buy
 * in the custom swap. `SWAP_TON_ASSET` is STON.fi's legacy pseudo-address for
 * native TON pool metadata; Omniston requests use a canonical native AssetId.
 */
export const SWAP_PRMD_MASTER: string =
  env.VITE_PRMD_JETTON_MASTER ||
  'EQA5WvRHM55HEvyNU3vLIbjrBz0CMGxIgNVov1aZJbqNDN8a';

export const SWAP_TON_ASSET = 'EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c';

export const SWAP_OMNISTON_API_URL: string =
  env.VITE_SWAP_OMNISTON_API_URL || 'wss://omni-ws.ston.fi';

export const SWAP_REFERRER_ADDRESS: string =
  env.VITE_SWAP_REFERRER_ADDRESS ?? '';

export const SWAP_REFERRER_FEE_BPS: number = Number(
  env.VITE_SWAP_REFERRER_FEE_BPS ?? '0',
);

/**
 * Whether the custom swap is shown on the PRMD page. It remains off by default
 * until limited-value canary transactions have been verified.
 */
export const SWAP_ENABLED: boolean =
  String(env.VITE_SWAP_ENABLED ?? '').toLowerCase() === 'true';

export const LIQUIDITY_ENABLED: boolean =
  String(env.VITE_LIQUIDITY_ENABLED ?? '').toLowerCase() === 'true';

export const SWAP_STON_POOL =
  'EQCqSu3IzHrbK_4WmA_PSsNehuGHWt7rruFsSzb6ebYYnO1V';

export const SWAP_STON_ROUTER =
  'EQCx0HDJ_DxLxDSQyfsEqHI8Rs65nygvdmeD9Ra7rY15OWN8';

export const SWAP_STON_PTON =
  'EQBnGWMCf3-FZZq1W4IWcWiGAc3PHuZ0_H-7sad2oY00o83S';

export const SWAP_STON_PTON_ROUTER_WALLET =
  'EQCzUiz7TFS7p7ByYXt-c3lJDmyGvmHTQIm0vhwSiiiaLpVj';

export const SWAP_STON_PRMD_ROUTER_WALLET =
  'EQADIsy4gs1rKIum3lpTVhHKteqdbZSEfyswzCdzcZg1qMg5';

/**
 * Last prize payout of the previous-generation lottery contract
 * (EQD_QzmHKgtGeXTpcPNOr4Up973fzqiUySM7cgULUyYBR3jT). Shown in the
 * "last winner" card until the current contract produces its own winner,
 * so the migration doesn't blank out the lottery's visible track record.
 */
export const LEGACY_LAST_WIN = {
  winnerAddress: 'EQBp5etI82UFBaKKMs_2QKNDT5hPct5NQbJmZW4Qf-75Y6ot',
  winnerKey: 2,
  amountNano: 34822853042n,
  time: 1784607694,
};

/**
 * Contract economics, mirrored from `contract/code_contracts/constants.tact`.
 * Used purely for display/derivation in the UI.
 */
export const CONTRACT_PARAMS = {
  maxSupply: 1_000_000_000, // PRMD
  emissionPerSecond: 1, // PRMD/sec
  jettonDecimals: 9,
  jettonSymbol: 'PRMD',
  referralLevels: 5,
  referralSharePercent: 20, // of the remainder, per level
  lotteryWinnerShare: 20, // % of bank to the winner
  lotteryOwnerShare: 5, // % of bank to the owner
  roundDurationDays: 7,
  minEntryTon: 1,
  ownerReferrerKey: 0,
} as const;

/** Block explorer base for the active network (tonviewer). */
export const EXPLORER_BASE: string =
  TON_NETWORK === 'mainnet' ? 'https://tonviewer.com' : 'https://testnet.tonviewer.com';

/** Build an explorer link for an address (string already in friendly form). */
export const explorerAddress = (addr: string): string => `${EXPLORER_BASE}/${addr}`;

/** Build an explorer link for a transaction (hash in hex). */
export const explorerTx = (hashHex: string): string => `${EXPLORER_BASE}/transaction/${hashHex}`;

/** Source-code verifier base for the active network (verifier.ton.org). */
export const VERIFIER_BASE: string =
  TON_NETWORK === 'mainnet'
    ? 'https://verifier.ton.org'
    : 'https://verifier.ton.org/testnet';

/** Build a verifier.ton.org link for a contract address. */
export const verifierAddress = (addr: string): string => `${VERIFIER_BASE}/${addr}`;
